import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  JournalApprovalDecision,
  JournalApprovalResultOutput,
} from "models/journal-approval";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StatusChip from "../general/StatusChip";
import { useJournalApprovalRequest } from "src/hooks/journal-approval/useJournalApprovalRequest";
import { displayJournalType, displayNsStatusName, getNetSuiteLink } from "src/util/helpers";

type NsJournalType = "Journal" | "AdvIntercompanyJournal";

function NsJournalLink({
  nsType,
  journalId,
  children,
}: {
  nsType: NsJournalType;
  journalId?: string;
  children: React.ReactNode;
}) {
  const id = String(journalId || "").trim();
  if (!id) {
    return <>{children}</>;
  }
  return (
    <a
      href={getNetSuiteLink(nsType, id)}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

const decisionColor = (
  decision: JournalApprovalDecision
): "success" | "error" | "warning" | "default" => {
  if (decision === "APPROVED" || decision === "ALREADY_APPROVED") {
    return "success";
  }
  if (decision === "FAILED" || decision === "APPROVER_NOT_CONFIGURED") {
    return "error";
  }
  if (decision === "AMBIGUOUS" || decision === "NO_RECORD_FOUND") {
    return "warning";
  }
  return "default";
};

const JournalApproval: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const { request, loading, refetch } = useJournalApprovalRequest(id!);

  const fetchRecord = useCallback(async (silent = false) => {
    if (id) {
      setRefreshing(true);
      try {
        await refetch(silent);
      } finally {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchRecord();
  }, []);

  useEffect(() => {
    if (request?.status === "IN_PROGRESS") {
      const intervalId = setInterval(() => {
        fetchRecord(true);
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [request?.status, fetchRecord]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!request) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography variant="body1">
          No journal approval record found.
        </Typography>
      </Box>
    );
  }

  const output = request.output as JournalApprovalResultOutput;
  const recordType = output.recordType || request.input.recordType || "";
  const typeLabel = displayJournalType(
    recordType,
    output.recordTypeLabel || request.input.recordTypeLabel
  );
  const inputTypeLabel = displayJournalType(
    request.input.recordType,
    request.input.recordTypeLabel
  );
  const nsType: NsJournalType =
    String(recordType).toLowerCase() === "advintercompanyjournalentry"
      ? "AdvIntercompanyJournal"
      : "Journal";
  const journalId = output.journalId || request.input.journalId || "";
  const journalNumber =
    request.input.journalNumber ||
    output.journalTranId ||
    request.input.journalTranId ||
    "";

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3} gap={2}>
          <IconButton
            onClick={() => navigate("/journal-approvals")}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">JEAPP-{request.requestId}</Typography>
          <StatusChip status={request.status} />
          <Chip
            label={request.output.decision}
            color={decisionColor(request.output.decision)}
          />
          {refreshing && <CircularProgress size={24} />}
        </Box>

        <Box mb={3}>
          <Typography variant="h6">Input Details</Typography>
          {journalNumber && (
            <Typography variant="body2">
              <strong>Journal Number:</strong>{" "}
              <NsJournalLink nsType={nsType} journalId={journalId}>
                {journalNumber}
              </NsJournalLink>
            </Typography>
          )}
          {journalId && (
            <Typography variant="body2">
              <strong>NetSuite ID:</strong>{" "}
              <NsJournalLink nsType={nsType} journalId={journalId}>
                {journalId}
              </NsJournalLink>
            </Typography>
          )}
          {request.input.journalTranId &&
            request.input.journalTranId.trim().toUpperCase() !==
              String(request.input.journalNumber || "")
                .trim()
                .toUpperCase() && (
              <Typography variant="body2">
                <strong>Number:</strong> {request.input.journalTranId}
              </Typography>
            )}
          {inputTypeLabel && (
            <Typography variant="body2">
              <strong>Type:</strong> {inputTypeLabel}
            </Typography>
          )}
          {(request.input.requesterName || request.input.requesterEmail) && (
            <Typography variant="body2">
              <strong>Requester:</strong>{" "}
              {request.input.requesterName || request.input.requesterEmail}
              {request.input.requesterEmail && request.input.requesterName
                ? ` (${request.input.requesterEmail})`
                : ""}
            </Typography>
          )}
        </Box>

        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Output Details
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">
              <strong>Decision:</strong>
            </Typography>
            <Chip
              label={request.output.decision}
              color={decisionColor(request.output.decision)}
            />
          </Box>
          {(output.approverName || output.approverEmail) && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Approver:</strong>{" "}
              {output.approverName || output.approverEmail}
            </Typography>
          )}
          {(output.createdByEmail || output.createdById) && (
            <Typography variant="body2">
              <strong>Created By:</strong>{" "}
              {output.createdByEmail || output.createdById}
              {output.createdByEmail && output.createdById
                ? ` (${output.createdById})`
                : ""}
            </Typography>
          )}
          {(output.nextApproverEmail || output.nextApproverId) && (
            <Typography variant="body2">
              <strong>Next Approver:</strong>{" "}
              {output.nextApproverEmail || output.nextApproverId}
              {output.nextApproverEmail && output.nextApproverId
                ? ` (${output.nextApproverId})`
                : ""}
            </Typography>
          )}
          {output.journalId && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Journal:</strong>{" "}
              <NsJournalLink nsType={nsType} journalId={output.journalId}>
                {output.journalTranId || output.journalId}
              </NsJournalLink>
            </Typography>
          )}
          {output.journalId && (
            <Typography variant="body2">
              <strong>NetSuite ID:</strong>{" "}
              <NsJournalLink nsType={nsType} journalId={output.journalId}>
                {output.journalId}
              </NsJournalLink>
            </Typography>
          )}
          {typeLabel && (
            <Typography variant="body2">
              <strong>Type:</strong> {typeLabel}
            </Typography>
          )}
          {output.nsStatusName && (
            <Typography variant="body2">
              <strong>NetSuite Status:</strong>{" "}
              {displayNsStatusName(output.nsStatusName)}
              {output.nsStatus ? ` (${output.nsStatus})` : ""}
            </Typography>
          )}
          {"reasoning" in output && output.reasoning && (
            <Typography variant="body2">
              <strong>Reasoning:</strong> {output.reasoning}
            </Typography>
          )}
          {"attempts" in output && output.attempts != null && (
            <Typography variant="body2">
              <strong>Attempts:</strong> {output.attempts}
            </Typography>
          )}
          {"workUnit" in output &&
            output.workUnit &&
            output.workUnit !== "JIRA_CREATE_FAILED" && (
              <Typography variant="body2">
                <strong>Work Unit:</strong>{" "}
                <a
                  href={`https://devgraph-alp.atlassian.net/browse/${output.workUnit}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {output.workUnit}
                </a>
              </Typography>
            )}
          {"workUnit" in output && output.workUnit === "JIRA_CREATE_FAILED" && (
            <Typography variant="body2">
              <strong>Work Unit:</strong> Jira create failed
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default JournalApproval;
